'use client';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';
import { apiGet, apiRequest, type ApiError } from '@/lib/api';
import { playerModelV2 } from '@/models/playerModelV2';
type Follows = {
  userId: string | null;
  viewerPlayerId: string | null;
  ready: boolean;
  playerIds: Set<string>;
  pending: Set<string>;
  error: boolean;
  /** The server refused this viewer (403: the organiser shows entrants only to entrants). Not an
   * error: following is simply not offered here, and nothing is ever ready to write. */
  unavailable: boolean;
  retry: () => void;
  setFollowing: (playerId: string, following: boolean) => Promise<void>;
};
const empty: Follows = {
  userId: null,
  viewerPlayerId: null,
  ready: false,
  playerIds: new Set(),
  pending: new Set(),
  error: false,
  unavailable: false,
  retry: () => {},
  setFollowing: async () => {},
};
const Context = createContext<Follows>(empty);
export const useEventFollows = () => useContext(Context);
/**
 * One store spans public event pages (it wraps every tab's `{children}`, so it must never remount them).
 *
 * The viewer comes from the browser's stored session: `onAuthStateChange` emits `INITIAL_SESSION` on
 * subscribe (read from storage, refreshed only if expired — no `/auth/v1/user` round trip), then every
 * sign-in/out. That id only decides which follows to *display*; it is never trusted for access. The follows
 * API verifies the Bearer token server-side (`auth.getUser()`) and reads/writes only that token's user,
 * and `expectedUserId` makes `apiRequest` refuse a call once the session belongs to someone else.
 */
export function EventFollowProvider({
  eventId,
  children,
}: {
  eventId: string;
  children: ReactNode;
}) {
  const [userId, setUserId] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    const client = createClient();
    if (!client) {
      setUserId(null);
      return;
    }
    let active = true;
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, session) => {
      if (active) setUserId(session?.user.id ?? null);
    });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);
  // Keyed on the event only: an account change resets the scope in place (below), so resolving the
  // stored session after hydration (undefined -> user) does not remount the page.
  return (
    <FollowScope key={eventId} eventId={eventId} userId={userId}>
      {children}
    </FollowScope>
  );
}
/** Everything that belongs to one account. A new account gets a new object, so nothing the previous
 * account started (a load, a write, its rollback, its locks) can reach it: every update checks `gates`. */
type Scope = {
  userId: string | null | undefined;
  gates: { locks: Set<string>; loading: boolean; reloadAfterWrites: boolean };
  playerIds: Set<string>;
  pending: Set<string>;
  viewerPlayerId: string | null;
  ready: boolean;
  error: boolean;
  unavailable: boolean;
  /** Bumped to re-read this account's follows. */
  attempt: number;
};
const scopeFor = (userId: string | null | undefined): Scope => ({
  userId,
  // `loading` closes the write gate synchronously; a retry must read after every pending write has
  // committed or rolled back (`reloadAfterWrites`).
  gates: { locks: new Set(), loading: userId !== null, reloadAfterWrites: false },
  playerIds: new Set(),
  pending: new Set(),
  viewerPlayerId: null,
  ready: userId === null,
  error: false,
  unavailable: false,
  attempt: 0,
});
function FollowScope({
  eventId,
  userId,
  children,
}: {
  eventId: string;
  userId: string | null | undefined;
  children: ReactNode;
}) {
  const [stored, setScope] = useState(() => scopeFor(userId));
  // Account changed: replace the scope during render, so no child ever renders A's follows for B.
  let scope = stored;
  if (stored.userId !== userId) {
    scope = scopeFor(userId);
    setScope(scope);
  }
  const { gates, attempt } = scope;
  const patch = (owner: Scope['gates'], change: (s: Scope) => Partial<Scope>) =>
    setScope((s) => (s.gates === owner ? { ...s, ...change(s) } : s));
  useEffect(() => {
    if (!userId) return;
    let active = true;
    gates.loading = true;
    patch(gates, () => ({ ready: false, error: false, unavailable: false }));
    const client = createClient();
    void apiGet<{ playerIds: string[] }>(`/api/event/${eventId}/follows`, {
      expectedUserId: userId,
    })
      .then((data) => {
        if (!active) return;
        gates.loading = false;
        patch(gates, () => ({ playerIds: new Set(data.playerIds), ready: true }));
      })
      .catch((err: ApiError) => {
        // A 403 is the visibility rule answering, not a failure: the write gate stays closed.
        if (active)
          patch(gates, () => (err?.status === 403 ? { unavailable: true } : { error: true }));
      });
    if (client)
      void playerModelV2
        .getByUserId(client, userId)
        .then((player) => {
          if (active) patch(gates, () => ({ viewerPlayerId: player?.id ?? null }));
        })
        .catch(() => {
          if (active) patch(gates, () => ({ viewerPlayerId: null }));
        });
    return () => {
      active = false;
    };
  }, [eventId, userId, attempt, gates]);
  const setFollowing = async (playerId: string, following: boolean) => {
    if (!userId || !scope.ready || gates.loading || gates.locks.has(playerId)) return;
    gates.locks.add(playerId);
    patch(gates, () => ({ pending: new Set(gates.locks), error: false }));
    const previous = scope.playerIds.has(playerId);
    const apply = (value: boolean) =>
      patch(gates, (s) => {
        const next = new Set(s.playerIds);
        if (value) next.add(playerId);
        else next.delete(playerId);
        return { playerIds: next };
      });
    apply(following);
    try {
      await apiRequest(`/api/event/${eventId}/follows`, {
        method: 'PUT',
        body: { playerId, following },
        expectedUserId: userId,
      });
    } catch {
      apply(previous);
      patch(gates, () => ({ error: true }));
    } finally {
      gates.locks.delete(playerId);
      patch(gates, () => ({ pending: new Set(gates.locks) }));
      if (gates.reloadAfterWrites && gates.locks.size === 0) {
        gates.reloadAfterWrites = false;
        patch(gates, (s) => ({ attempt: s.attempt + 1 }));
      }
    }
  };
  const retry = () => {
    if (!userId) return;
    // Close the write gate synchronously, including calls made before the next render.
    gates.loading = true;
    patch(gates, () => ({ ready: false, error: false, unavailable: false }));
    if (gates.locks.size > 0) gates.reloadAfterWrites = true;
    else patch(gates, (s) => ({ attempt: s.attempt + 1 }));
  };
  return (
    <Context.Provider
      value={{
        userId: userId ?? null,
        viewerPlayerId: scope.viewerPlayerId,
        ready: scope.ready,
        playerIds: scope.playerIds,
        pending: scope.pending,
        error: scope.error,
        unavailable: scope.unavailable,
        retry,
        setFollowing,
      }}
    >
      {children}
    </Context.Provider>
  );
}
